import React, { useState, useEffect, useCallback } from 'react';
import { getAuthToken } from '@/lib/api';
import { useAuth } from '@/lib/auth-context';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import { Upload, FileText, Download, Trash2, FolderOpen, File, X } from 'lucide-react';
import { toast } from 'sonner';
import logger from '@/lib/logger';

const DOCUMENT_FOLDERS = {
  KYC: ['PAN Card', 'Aadhaar Card', 'GST Certificate', 'TAN Certificate', 'Incorporation Certificate', 'Address Proof', 'Bank Details', 'Board Resolution', 'Other'],
  Data: ['Bank Statements', 'Trial Balance', 'Ledgers', 'Sales Invoices', 'Purchase Records', 'Salary Statements', 'Stock Records', 'Loan Documents', 'Other'],
  Filing: ['GST Returns', 'Income Tax Returns', 'TDS Returns', 'ROC Filings', 'Audit Reports', 'Certificates', 'Computation Sheets', 'Acknowledgements', 'Other']
};

const TAB_LABELS = {
  KYC: 'KYC Documents',
  Data: 'Data',
  Filing: 'Filing Documents',
};

const TAB_DESCRIPTIONS = {
  KYC: 'Identity & registration documents',
  Data: 'Documents shared by client',
  Filing: 'Uploaded by CA team after compliance',
};

const DocumentsPage = ({ clientId, clientName }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadForm, setUploadForm] = useState({
    category: 'KYC',
    folder: 'Other',
    description: ''
  });
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState('KYC');

  const canManage = ['admin', 'staff'].includes(user?.role);

  const loadDocuments = useCallback(async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/documents`, {
        headers: { 'Authorization': `Bearer ${getAuthToken()}` },
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setDocuments(data);
      }
    } catch (error) {
      logger.error('Failed to load documents:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    if (clientId) {
      loadDocuments();
    }
  }, [clientId, loadDocuments]);

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 10MB)
      if (file.size > 10 * 1024 * 1024) {
        toast.error('File size must be less than 10MB');
        return;
      }
      setSelectedFile(file);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('category', uploadForm.category);
      formData.append('folder', uploadForm.folder);
      if (uploadForm.description) {
        formData.append('description', uploadForm.description);
      }

      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/documents/upload`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${getAuthToken()}` },
          credentials: 'include',
          body: formData
        }
      );

      if (response.ok) {
        toast.success('Document uploaded successfully');
        setUploadDialogOpen(false);
        setSelectedFile(null);
        setUploadForm({ category: 'KYC', folder: 'Other', description: '' });
        loadDocuments();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      logger.error(error);
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleDownload = async (doc) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/documents/${doc.id}/download`,
        { headers: { 'Authorization': `Bearer ${getAuthToken()}` }, credentials: 'include' }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = doc.original_filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
        toast.success('Document downloaded');
      } else {
        toast.error('Download failed');
      }
    } catch (error) {
      logger.error(error);
      toast.error('Download failed');
    }
  };

  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete ${doc.original_filename}?`)) return;
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/clients/${clientId}/documents/${doc.id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${getAuthToken()}` },
          credentials: 'include',
        }
      );

      if (response.ok) {
        toast.success('Document deleted');
        loadDocuments();
      } else {
        toast.error('Delete failed');
      }
    } catch (error) {
      logger.error(error);
      toast.error('Delete failed');
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const filterDocuments = (category) => {
    return documents.filter(doc => doc.category === category);
  };

  const groupByFolder = (docs) => {
    const grouped = {};
    docs.forEach(doc => {
      if (!grouped[doc.folder]) {
        grouped[doc.folder] = [];
      }
      grouped[doc.folder].push(doc);
    });
    return grouped;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold">Documents</h2>
          <p className="text-sm text-muted-foreground">{clientName}</p>
        </div>
        <Button onClick={() => setUploadDialogOpen(true)} data-testid="upload-document-button">
          <Upload className="h-4 w-4 mr-2" /> Upload Document
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="KYC" data-testid="doc-tab-kyc">KYC Documents</TabsTrigger>
          <TabsTrigger value="Data" data-testid="doc-tab-data">Data</TabsTrigger>
          <TabsTrigger value="Filing" data-testid="doc-tab-filing">Filing Documents</TabsTrigger>
        </TabsList>

        {['KYC', 'Data', 'Filing'].map(category => (
          <TabsContent key={category} value={category} className="space-y-4">
            <p className="text-xs text-muted-foreground">{TAB_DESCRIPTIONS[category]}</p>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-20 bg-muted rounded-lg animate-pulse" />)}
              </div>
            ) : (
              <>
                {Object.entries(groupByFolder(filterDocuments(category))).length === 0 ? (
                  <Card>
                    <CardContent className="p-8 text-center">
                      <FolderOpen className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                      <h3 className="font-semibold mb-1">No documents yet</h3>
                      <p className="text-sm text-muted-foreground">Upload your first {category} document to get started</p>
                    </CardContent>
                  </Card>
                ) : (
                  Object.entries(groupByFolder(filterDocuments(category))).map(([folder, docs]) => (
                    <Card key={folder}>
                      <CardHeader>
                        <CardTitle className="text-base flex items-center gap-2">
                          <FolderOpen className="h-4 w-4" />
                          {folder}
                          <Badge variant="secondary" className="ml-auto">{docs.length}</Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          {docs.map(doc => (
                            <div key={doc.id} className="flex items-center gap-3 p-3 rounded-lg border hover:bg-muted/50 transition-colors">
                              <File className="h-8 w-8 text-primary flex-shrink-0" />
                              <div className="flex-1 min-w-0">
                                <p className="font-medium text-sm truncate">{doc.original_filename}</p>
                                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                  <span>{formatFileSize(doc.file_size)}</span>
                                  <span>•</span>
                                  <span>{formatDate(doc.uploaded_at)}</span>
                                </div>
                                {doc.description && (
                                  <p className="text-xs text-muted-foreground mt-1">{doc.description}</p>
                                )}
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDownload(doc)}
                                  data-testid="download-document-button"
                                >
                                  <Download className="h-4 w-4" />
                                </Button>
                                {canManage && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDelete(doc)}
                                    data-testid="delete-document-button"
                                  >
                                    <Trash2 className="h-4 w-4 text-destructive" />
                                  </Button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
        <DialogContent data-testid="upload-document-dialog">
          <DialogHeader>
            <DialogTitle>Upload Document</DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Category</Label>
                <Select
                  value={uploadForm.category}
                  onValueChange={(value) => {
                    setUploadForm({ ...uploadForm, category: value, folder: DOCUMENT_FOLDERS[value][0] });
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KYC">KYC Documents</SelectItem>
                    <SelectItem value="Data">Data (Client Shared)</SelectItem>
                    <SelectItem value="Filing">Filing Documents (CA Team)</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Folder</Label>
                <Select
                  value={uploadForm.folder}
                  onValueChange={(value) => setUploadForm({ ...uploadForm, folder: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {DOCUMENT_FOLDERS[uploadForm.category].map(folder => (
                      <SelectItem key={folder} value={folder}>{folder}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="description">Description (Optional)</Label>
              <Textarea
                id="description"
                value={uploadForm.description}
                onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
                placeholder="Add a description..."
                rows={2}
              />
            </div>

            <div className="border-2 border-dashed rounded-lg p-6 text-center">
              {selectedFile ? (
                <div className="space-y-2">
                  <FileText className="h-12 w-12 text-primary mx-auto" />
                  <div className="text-sm font-medium">{selectedFile.name}</div>
                  <div className="text-xs text-muted-foreground">{formatFileSize(selectedFile.size)}</div>
                  <Button variant="ghost" size="sm" onClick={() => setSelectedFile(null)}>
                    <X className="h-4 w-4 mr-1" /> Remove
                  </Button>
                </div>
              ) : (
                <div className="space-y-2">
                  <Upload className="h-12 w-12 text-muted-foreground mx-auto" />
                  <Label htmlFor="file-upload" className="cursor-pointer text-sm font-medium text-primary hover:underline">
                    Click to select file
                  </Label>
                  <input
                    id="file-upload"
                    type="file"
                    onChange={handleFileSelect}
                    className="hidden"
                    data-testid="file-input"
                  />
                  <p className="text-xs text-muted-foreground">Max file size: 10MB</p>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setUploadDialogOpen(false)} disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={handleUpload} disabled={!selectedFile || uploading} data-testid="upload-button">
                {uploading ? 'Uploading...' : 'Upload'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DocumentsPage;
